import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { StateValues } from 'react-use-form-state';
import {
  AdminApi,
  AdminProductDetail,
  AdminProductOverview,
  ChangeTypeEnum,
  DocumentsApi,
  PatchedAdminProductDetailRequest,
} from 'api';
import useApiCall from 'hooks/use-api-call';
import Translate from 'ui/atoms/translate';
import { ActionButton } from 'ui/molecules/action-buttons';
import { toMoney } from 'ui/helper/money';
import AddProductModal, { AddProductFields } from 'apps/issuer/pages/products/add-product-modal';

interface EditProductProps {
  onClose: () => void;
  data: Omit<AdminProductOverview, 'network'> & AdminProductDetail;
  onEditProductSuccess: () => void;
  canEditProduct: boolean;
  canEditIssuanceDocuments: boolean;
  isProductValidated?: boolean;
  isProductUnderValidation?: boolean;
}

const EditProduct: FunctionComponent<EditProductProps> = ({
  onClose,
  data,
  onEditProductSuccess,
  canEditProduct,
  canEditIssuanceDocuments,
  isProductValidated,
  isProductUnderValidation,
}) => {
  const { makeAuthenticatedApi, withApi, loading: apiLoading, error } = useApiCall();

  const api: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);
  const documentsApi: DocumentsApi = useMemo(() => makeAuthenticatedApi(DocumentsApi), [makeAuthenticatedApi]);

  const modalHeader = useMemo(() => {
    return <Translate name="addProduct.editGeneralInformation" />;
  }, []);

  const actionButtons = useMemo((): ActionButton[] => {
    return [
      {
        name: 'cancel',
        content: <Translate name="common.cancel" />,
        size: 'large',
        onClick: () => onClose(),
      },
      {
        name: 'send',
        content: <Translate name="common.saveChanges" />,
        variant: 'primary',
        loading: apiLoading,
        type: 'submit',
        size: 'large',
      },
    ];
  }, [apiLoading]);

  const onSubmit = useCallback(
    (fieldsValues: StateValues<AddProductFields>, dateAndTime?: Date) => {
      withApi(async () => {
        let params: PatchedAdminProductDetailRequest = {};
        if (canEditProduct) {
          if (isProductValidated || isProductUnderValidation || fieldsValues.ewpg === 'false') {
            params = {
              issuerWalletAddress: fieldsValues.issuerWalletAddress,
            };
          } else if (fieldsValues.ewpg === 'true') {
            params = {
              name: fieldsValues.name,
              productType: fieldsValues.productType,
              code: fieldsValues.assetCode,
              isin: fieldsValues.isin,
              issuerWalletAddress: fieldsValues.issuerWalletAddress,
              backupWallet: fieldsValues.backupWallet,
              maxIssuableAmount: Number(fieldsValues.maxIssuableAmount),
              stepSize: Number(fieldsValues.stepSize),
              paymentDeadline: Number(fieldsValues.paymentDeadline),
              cancellationDeadline: Number(fieldsValues.cancellationDeadline),
              calculationFactor: fieldsValues.calculationFactor && fieldsValues.calculationFactor.toString(),
              provideNominalValue: fieldsValues.provideNominalValue === 'true',
              nominalValue:
                fieldsValues.nominalValue && fieldsValues.provideNominalValue === 'true'
                  ? typeof fieldsValues.nominalValue === 'number' || typeof fieldsValues.nominalValue === 'string'
                    ? toMoney(Number(fieldsValues.nominalValue))
                    : fieldsValues.nominalValue
                  : null,
              issuancePrice: fieldsValues.issuancePrice
                ? typeof fieldsValues.issuancePrice === 'number' || typeof fieldsValues.issuancePrice === 'string'
                  ? toMoney(Number(fieldsValues.issuancePrice))
                  : fieldsValues.issuancePrice
                : null,
              ewpg: fieldsValues.ewpg === 'true',
              publicationRestricted: fieldsValues.publicationRestricted === 'true',
            };

            if (dateAndTime) {
              params.lockInTime = dateAndTime;
            }
            if (fieldsValues.isin.length === 0) {
              params.isin = null;
            }
            if (fieldsValues.ewpg === 'true') {
              delete params.backupWallet;
            }
          }
        }

        if (fieldsValues.issuanceDocument?.file) {
          const docResponse = await documentsApi.adminDocumentsCreate({
            file: fieldsValues.issuanceDocument?.file,
          });

          if (!isProductUnderValidation) {
            await api.adminProductsIssuanceDocumentCreate({
              id: data.id,
              adminVersionedIssuanceDocumentRequest: {
                documentId: docResponse.id,
                ...(isProductValidated && { changeType: fieldsValues.changeType || ChangeTypeEnum.MINOR }),
              },
            });
          }
        }

        await api.adminProductsPartialUpdate({ id: data.id, patchedAdminProductDetailRequest: params });
        onEditProductSuccess();
      });
    },
    [withApi, api],
  );

  return (
    <AddProductModal
      isInfoView={false}
      isExitingView={false}
      onSubmit={onSubmit}
      onClose={onClose}
      modalHeader={modalHeader}
      actionButtons={actionButtons}
      error={error}
      data={data}
      canEditProduct={canEditProduct}
      canEditIssuanceDocuments={canEditIssuanceDocuments}
      isProductValidated={isProductValidated}
      isProductUnderValidation={isProductUnderValidation}
    />
  );
};

export default EditProduct;
